import React, { useContext, useEffect } from 'react';
import get from 'lodash/get';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useReactiveVar } from '@apollo/client';
import { StringParam, useQueryParam } from 'use-query-params';

import Scrollbar from 'vibo-ui/Scrollbar';
import LoadingOverlay from 'components/common/LoadingOverlay';
import NoSectionsYet from 'components/emptyStates/NoSectionsYet';
import NoSearchResults from 'components/emptyStates/NoSearchResults';
import AddSectionInMiddle from '../TimelineSection/AddSectionInMiddle';
import CreateSectionButton from 'components/buttons/CreateSectionButton';
import List, { DragResponse } from 'vibo-ui/List';
import { EventContext } from 'components/context/EventContext';
import { SectionsFilterableContext } from 'components/events/Timeline/SectionsFilterContext';

import client from 'graphql/client';
import { isLoadingVar } from 'graphql/cache';
import { scrollToSelectedSection } from './constants';
import { hasSectionsFilterValue } from 'services/sections/helpers';

import { SectionType } from 'types/enums';

import { SectionsProps } from './interfaces';
import { DndTypes } from 'components/common/Dnd';

import useStyles from './style';

const Sections = <TSection extends SectionBase, TReorderVariables = any>({
  sections,
  reorderProps,
  timelineClassName,
  isSectionsLoading,
  renderHeader,
  renderSection,
  renderTimelineItem,
}: SectionsProps<TSection, TReorderVariables>): Nullable<React.ReactElement> => {
  const classes = useStyles();
  const { t } = useTranslation();
  const isLoading = useReactiveVar(isLoadingVar);

  const filterContext = useContext(SectionsFilterableContext);
  const {
    isLoading: isEventLoading,
    event,
    isFavoriteSections,
    canAddSection = false,
    canReorderSections = false,
  } = useContext(EventContext);

  const [sectionsQ] = useQueryParam('sectionsQ', StringParam);

  const onDragEnd = (reorderedSections: TSection[], { removedIndex, addedIndex }: DragResponse) => {
    if (
      hasSectionsFilterValue(
        get(reorderProps, 'variables.filter') as Maybe<Nullable<SectionsFilter>>
      )
    ) {
      return;
    }

    if (!reorderProps) {
      return;
    }

    const { onReorder: reorderSections, listQuery, listCacheKey, variables } = reorderProps;

    client.writeQuery({
      variables,
      query: listQuery,
      data: {
        [listCacheKey]: reorderedSections,
      },
    });

    reorderProps?.onDragEnd?.(sections[removedIndex]);

    return reorderSections({
      variables: {
        ...variables,
        sourceSectionId: sections[removedIndex]._id,
        targetSectionId: addedIndex < 0 ? null : sections[addedIndex]._id,
      },
    });
  };

  useEffect(() => {
    scrollToSelectedSection();
  }, []);

  return (
    <div
      className={classNames('sections', classes.sections, {
        [classes.nonEmptyList]: !!sections.length,
        [classes.emptyList]: !sections.length,
      })}
    >
      {renderHeader()}
      {(isSectionsLoading || isEventLoading) && !sections.length ? (
        <LoadingOverlay fillOverlay={false} />
      ) : !sections.length ? (
        !!sectionsQ ? (
          <NoSearchResults />
        ) : isLoading ? null : (
          <NoSectionsYet>
            {t('noSectionsYet')}
            {canAddSection ? (
              <CreateSectionButton
                eventSettings={event?.settings as EventSettings}
                displayType="primary"
                shape="round"
                size="lg"
                key="empty-state-add-section-btn"
              >
                {t('addSection')}
              </CreateSectionButton>
            ) : null}
          </NoSectionsYet>
        )
      ) : (
        <Scrollbar id="sectionsTimelineScroll">
          <List<TSection>
            renderItem={(section, index) => {
              const prevSection = sections[index - 1];
              const nextSection = sections[index + 1];

              const isDontPlay = section.type === SectionType.dontPlay;
              const isHeadline = section.type === SectionType.headline;
              const isPrevHeadline = prevSection?.type === SectionType.headline;

              const isStart =
                !prevSection ||
                // @ts-ignore
                (isHeadline && !!section.sectionImage?.imageUrl) ||
                // @ts-ignore
                (isPrevHeadline && !prevSection?.sectionImage?.imageUrl);

              const isEnd = !nextSection || nextSection.type === SectionType.headline;

              const content = renderSection({
                section,
                index,
                className: classNames({
                  isStart,
                  isEnd,
                  firstSection: !index,
                }),
              });

              return !!renderTimelineItem ? (
                renderTimelineItem(content, section, isDontPlay)
              ) : (
                <>{content}</>
              );
            }}
            data={sections}
            onDropCallback={onDragEnd}
            draggable={canReorderSections && !filterContext?.filter?.q && !!reorderProps}
            dndType={DndTypes.SECTION}
            className={classNames(classes.sectionsTimeline, timelineClassName)}
            scrollId="sectionsTimelineScroll"
            dragByHandler
          >
            {canAddSection && !isFavoriteSections ? <AddSectionInMiddle sectionId={null} /> : null}
          </List>
        </Scrollbar>
      )}
    </div>
  );
};

export default Sections;
